import moment from 'moment';

/**
 * Backend 205 returns role on portalUserConf (`role`), while 201 nests it in `user.roles`.
 * Normalize so the rest of the app can rely on user.roles.
 */
export const normalizePortalUser = (portalUser) => {
  if (!portalUser) return portalUser;

  const existingRoles = portalUser.user?.roles;
  if (Array.isArray(existingRoles) && existingRoles.length > 0) {
    return portalUser;
  }

  if (!portalUser.role) {
    return portalUser;
  }

  return {
    ...portalUser,
    user: {
      ...(portalUser.user || {}),
      roles: [portalUser.role]
    }
  };
};

export const normalizeLoginResult = (loginResult) => {
  if (!loginResult?.portalUserList) return loginResult;

  return {
    ...loginResult,
    portalUserList: loginResult.portalUserList.map(normalizePortalUser)
  };
};

export const getPortalUserRole = (portalUser) => {
  const normalized = normalizePortalUser(portalUser);
  return normalized?.user?.roles?.[0] || normalized?.role || null;
};

export const getStoredAgency = () => {
  try {
    return JSON.parse(localStorage.getItem('agency'));
  } catch {
    return null;
  }
};

/**
 * Role name of the connected user, read from the persisted agency so it
 * survives a page reload (Redux login state does not).
 * @returns {string|null} Role name (e.g. 'TE_ADMIN') or null when unknown.
 */
export const getStoredRoleName = () => getPortalUserRole(getStoredAgency())?.name || null;

export const getPortalUserMenu = (portalUser) => {
  const role = getPortalUserRole(portalUser);
  return Array.isArray(role?.menu) ? role.menu : null;
};

export const persistAgency = (agency) => {
  localStorage.setItem('agency', JSON.stringify(agency));
  const menu = getPortalUserMenu(agency);
  if (menu) {
    localStorage.setItem('leftNavMenu', JSON.stringify(menu));
  }
};

/**
 * Reads the raw `lastLogin` timestamp from a login payload.
 * The backend exposes it either on the portal user (`user.lastLogin`) or at the root of the
 * login response, so both shapes are accepted.
 * @param {Object} source - Login response or portal user
 * @returns {string|null} Raw timestamp, or null when absent
 * @author armandoricosandoval -ai-
 */
export const readLastLogin = (source) => source?.user?.lastLogin || source?.lastLogin || null;

/**
 * Formats a raw `lastLogin` timestamp for display.
 * @param {string|null} lastLogin - Raw timestamp read with {@link readLastLogin}
 * @returns {string|null} Display string, or null when absent or unparseable
 * @author armandoricosandoval -ai-
 */
export const formatLastLogin = (lastLogin) => {
  if (!lastLogin) return null;

  const parsedLastLogin = moment(lastLogin);
  return parsedLastLogin.isValid() ? parsedLastLogin.format('DD-MM-YYYY HH:mm:ss') : null;
};

/**
 * Backfills `lastLogin` on every portal user of a login result.
 * `lastLogin` is tied to the user record, not to the organization, so the same value applies to
 * every portal user. Backfilling keeps the dashboard able to render it when the login-data
 * endpoint omits the field and only the org-validation response carried it.
 * Portal users that already carry a value are left untouched.
 * @param {Object} loginResult - Login result with a portalUserList
 * @param {string|null} lastLogin - Display string to backfill
 * @returns {Object} Login result whose portal users carry lastLogin
 * @author armandoricosandoval -ai-
 */
export const withLastLogin = (loginResult, lastLogin) => {
  if (!lastLogin || !Array.isArray(loginResult?.portalUserList)) return loginResult;

  return {
    ...loginResult,
    portalUserList: loginResult.portalUserList.map((portalUser) =>
      portalUser?.user?.lastLogin ? portalUser : { ...portalUser, user: { ...(portalUser?.user || {}), lastLogin } }
    )
  };
};

/**
 * Tells whether a portal user belongs to a given organization.
 * Single definition of the comparison so the org lookup cannot diverge between callers.
 * @param {Object} portalUser - Portal user to check
 * @param {string|number} orgId - Organization to match
 * @returns {boolean} True when the portal user belongs to that organization
 * @author armandoricosandoval -ai-
 */
export const isPortalUserOfOrg = (portalUser, orgId) => portalUser?.organizationEntity?.orgId === orgId;

/**
 * Finds the portal user of a given organization.
 * @param {Array} portalUserList - Portal users to search
 * @param {string|number} orgId - Organization to match
 * @returns {Object|null} Matching portal user, or null when there is none
 * @author armandoricosandoval -ai-
 */
export const findPortalUserByOrgId = (portalUserList, orgId) =>
  (Array.isArray(portalUserList) ? portalUserList : []).find((portalUser) => isPortalUserOfOrg(portalUser, orgId)) || null;

const isPresent = (value) => value !== undefined && value !== null;

const firstPresent = (sources, key) =>
  sources
    .filter(Boolean)
    .map((source) => source[key])
    .find(isPresent);

export const applyOrgExtraFields = (organizationEntity, portalUser, ...sources) => {
  const autoEmployeeId = firstPresent(sources, 'autoEmployeeId');
  const officeName = firstPresent(sources, 'officeName');
  const clientName = firstPresent(sources, 'clientName');

  if (autoEmployeeId !== undefined) organizationEntity.autoEmployeeId = autoEmployeeId;
  if (officeName) portalUser.officeName = officeName;
  if (clientName) portalUser.clientName = clientName;
};

export const pickOrgExtraFields = (root, orgEntity = root) => ({
  autoEmployeeId: orgEntity?.autoEmployeeId,
  officeName: root?.officeName,
  clientName: root?.clientName
});

/**
 * Rebuilds a role entity from the flat `roleName` + `menu` pair of the v2 login response.
 * Role resolution downstream reads `user.roles[0].name` and `role.menu`, so the plain string
 * has to become an entity or the dashboard and the left nav cannot resolve the role.
 * @param {Object} response - Login-data response
 * @returns {Object|null} Role entity with name and menu, or null when no role is present
 * @author armandoricosandoval -ai-
 */
const toRoleEntity = (response) => {
  const name = response?.roleName || response?.role?.name || null;
  if (!name) return null;

  const menu = Array.isArray(response.menu) ? response.menu : response?.role?.menu;
  return { name, menu: Array.isArray(menu) ? menu : [] };
};

/**
 * Normalizes a login-data response into the { portalUserList, menuEntityList } shape the app
 * relies on. Accepts the legacy payload, which nests a full portalUserList under `result`, and
 * the v2 payload, which is flat and scoped to a single organization (`user`, `roleName`, `menu`).
 * For the v2 shape the resolved user is merged into the organization it belongs to, so the rest
 * of the list keeps whatever it already had. `parameterEntityList` stays on the login result
 * because tax allowance validation reads it from `tempEdge.login`.
 * @param {Object} response - Raw login-data response
 * @param {Object} [options] - Merge options
 * @param {string|number} [options.orgId] - Organization the response belongs to
 * @param {Array} [options.portalUserList] - Portal users already known from the token step
 * @returns {Object|null} Login result, or null when the response carries no usable data
 * @author armandoricosandoval -ai-
 */
export const toLoginResult = (response, { orgId, portalUserList } = {}) => {
  const legacyResult = response?.result;
  if (Array.isArray(legacyResult?.portalUserList)) return legacyResult;

  if (!response?.user && !response?.roleName) return null;

  const role = toRoleEntity(response);
  const orgUser = { ...(response.user || {}) };
  if (role) orgUser.roles = [role];

  const baseList = Array.isArray(portalUserList) ? portalUserList : [];
  const existingOrgUser = findPortalUserByOrgId(baseList, orgId);
  const organizationEntity = {
    ...existingOrgUser?.organizationEntity,
    ...response.organizationEntity,
    orgId
  };
  const resolvedOrgUser = {
    organizationEntity,
    user: orgUser,
    role,
    status: response.status || existingOrgUser?.status || 'A',
    portalUserConfId: response.portalUserConfId ?? existingOrgUser?.portalUserConfId
  };
  applyOrgExtraFields(
    organizationEntity,
    resolvedOrgUser,
    pickOrgExtraFields(response, response.organizationEntity),
    pickOrgExtraFields(response),
    pickOrgExtraFields(existingOrgUser, existingOrgUser?.organizationEntity)
  );

  const merged = existingOrgUser
    ? baseList.map((portalUser) => (isPortalUserOfOrg(portalUser, orgId) ? { ...portalUser, ...resolvedOrgUser } : portalUser))
    : [...baseList, resolvedOrgUser];

  const loginResult = {
    portalUserList: merged,
    menuEntityList: Array.isArray(response.menu) ? response.menu : []
  };

  if (response.parameterEntityList) {
    loginResult.parameterEntityList = response.parameterEntityList;
  }

  return loginResult;
};
